import {
  CreateDoctorAppointmentRequestCommand,
  DoctorAppointmentRequestsClient,
} from "../../web-api-client";
import { toast } from "../../hooks/use-toast";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { toUserMessage } from "../../utils/toUserMessage";
import { X } from "lucide-react";
import { useState } from "react";
import { useString } from "../../hooks/useString";
import Alert from "../ui/Alert";
import { useLanguage } from "../../i18n/LanguageContext";
import { appConfig } from "../../appconfig";

type ContactBookingForm = {
  fullName: string;
  phone: string;
  email: string;
  appointmentDate: string;
  note: string;
};

interface ContactBookingModalProps {
  open: boolean;
  onClose: () => void;
  doctorId: string;
  doctorName?: string;
}

export function ContactBookingModal({
  open,
  onClose,
  doctorId,
  doctorName,
}: ContactBookingModalProps) {
  const { t } = useLanguage();

  const [form, setForm] = useState<ContactBookingForm>({
    fullName: "",
    phone: "",
    email: "",
    appointmentDate: "",
    note: "",
  });
  const [errors, setErrors] = useState<{
    phone?: string;
    name?: string;
    submit?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);

  const updateField = (key: keyof ContactBookingForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === "phone") {
      setErrors((prev) => ({ ...prev, phone: undefined }));
    }
    if (key === "fullName") {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nextErrors: {
      phone?: string;
      name?: string;
      submit?: string;
    } = {};

    if (!form.fullName.trim()) {
      nextErrors.name = t("enterFullName");
    }

    if (!form.phone.trim()) {
      nextErrors.phone = t("enterPhone");
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    setIsSubmitSuccess(false);
    setErrors({});

    const client = new DoctorAppointmentRequestsClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });

    try {
      const command = new CreateDoctorAppointmentRequestCommand();
      command.fullName = form.fullName.trim() || undefined;
      command.phone = form.phone.trim();
      command.email = form.email.trim() || undefined;
      command.preferredDate = form.appointmentDate
        ? new Date(form.appointmentDate)
        : undefined;
      command.note = form.note.trim() || undefined;
      command.doctorId = doctorId || undefined;

      await client.createDoctorAppointmentRequest(command);

      setForm({
        fullName: "",
        phone: "",
        email: "",
        appointmentDate: "",
        note: "",
      });

      toast({
        variant: "success",
        title: t("successTitle"),
        description: t("savedContact"),
        duration: 3000,
      });
      setIsSubmitSuccess(true);
    } catch (err: any) {
      console.log(err.response);
      const msg = await toUserMessage(err);
      toast({
        variant: "destructive",
        title: t("submitFailed"),
        description: msg,
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl mb-20 md:mb-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <span className="text-lg font-bold text-[#1F7A3D]">
              {t("contactBookingTitle")}
            </span>
            <p className="mt-1 text-sm text-gray-500">
              {doctorName
                ? `${t("chuyengia")}: ${doctorName}.`
                : t("contactBookingTitle")}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isSubmitSuccess && (
          <Alert variant="info" msg={t("contactSaved")} className="mx-4 mt-4 " />
        )}

        <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4">
          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t("fullName")} <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              value={form.fullName}
              onChange={(e) => updateField("fullName", e.target.value)}
              className={`w-full rounded-xl border px-4 py-3 outline-none focus:ring-2 ${
                errors.name
                  ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                  : "border-gray-300 focus:border-[#1F7A3D] focus:ring-green-100"
              }`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t("phone")} <span className="text-red-500">*</span>
            </span>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center border-r border-gray-200 mt-2 mb-2">
                <div className="flex items-center gap-2 pl-3 pr-3 ">
                  <span
                    className="fi fi-vn"
                    style={{
                      width: 20,
                      height: 20,
                      display: "inline-block",
                    }}
                  />
                  <span className="text-gray-700 text-sm font-medium">+84</span>
                </div>
              </div>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="tel-national"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                className={`w-full pl-[90px] rounded-xl border px-4 py-3 outline-none focus:ring-2 ${
                  errors.phone
                    ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                    : "border-gray-300 focus:border-[#1F7A3D] focus:ring-green-100"
                }`}
              />
            </div>

            {errors.phone && (
              <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
            )}
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t("appointmentDate")}
            </span>
            <input
              type="date"
              value={form.appointmentDate}
              onChange={(e) => updateField("appointmentDate", e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-[#1F7A3D] focus:ring-2 focus:ring-green-100"
            />
          </div>

          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t("note")}
            </span>
            <textarea
              rows={4}
              value={form.note}
              onChange={(e) => updateField("note", e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-[#1F7A3D] focus:ring-2 focus:ring-green-100"
            />
          </div>

          {errors.submit && (
            <p className="text-sm text-red-500">{errors.submit}</p>
          )}

          <div className="flex gap-3 pt-2 flex-row justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="
                w-full max-w-[80%] md:max-w-xs rounded-xl
                bg-[linear-gradient(180deg,#34a853_0%,#2f9e4d_100%)]
                px-5 py-3
                text-sm md:text-base font-semibold text-white
                shadow-[0_8px_20px_rgba(47,125,68,0.22)]
                transition-all duration-200
                hover:brightness-[1.02] hover:shadow-[0_10px_24px_rgba(47,125,68,0.28)]
                active:scale-[0.995]
                disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none
              "
            >
              <span>{isSubmitting ? t("sending") : t("submit")}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
